import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { fuelService } from '../../services/fuel.service';
import { tripService } from '../../services/trip.service';
import { vehicleService } from '../../services/vehicle.service';
import { RecordFuelRequest, TransactionType } from '../../types';

interface RecordFuelModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId?: number;
}

const schema = z.object({
  tripId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1, 'Trip is required')
  ),
  vehicleId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1, 'Vehicle is required')
  ),
  fuelDate: z.string().min(1, 'Fuel date is required'),
  quantityLiters: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Quantity must be greater than 0')
  ),
  costPerLiter: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Cost per liter must be greater than 0')
  ),
  transactionType: z.nativeEnum(TransactionType),
  notes: z.string().optional(),
});

type FormInput = z.infer<typeof schema>;

export const RecordFuelModal: React.FC<RecordFuelModalProps> = ({ isOpen, onClose, tripId }) => {
  const queryClient = useQueryClient();

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: tripService.getAll,
    enabled: isOpen,
  });

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: vehicleService.getActive,
    enabled: isOpen,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      fuelDate: new Date().toISOString().split('T')[0],
      transactionType: TransactionType.PURCHASED,
    },
  });

  const selectedTripId = useWatch({ control, name: 'tripId' });

  useEffect(() => {
    if (!isOpen) {
      reset({
        fuelDate: new Date().toISOString().split('T')[0],
        transactionType: TransactionType.PURCHASED,
      });
    } else if (tripId) {
      setValue('tripId', tripId);
    }
  }, [isOpen, reset, tripId, setValue]);

  useEffect(() => {
    if (selectedTripId && trips) {
      const selectedTrip = trips.find(t => t.id === Number(selectedTripId));
      if (selectedTrip) {
        setValue('vehicleId', selectedTrip.vehicleId);
      }
    }
  }, [selectedTripId, trips, setValue]);

  const mutation = useMutation({
    mutationFn: (data: RecordFuelRequest) => fuelService.record(data),
    onSuccess: async () => {
      toast.success('Fuel recorded successfully');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['fuel'] }),
        queryClient.invalidateQueries({ queryKey: ['fuel', 'flagged'] }),
      ]);
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to record fuel');
    },
  });

  const onSubmit = (data: FormInput) => {
    const parsed: RecordFuelRequest = schema.parse(data);
    mutation.mutate({
      ...parsed,
      notes: parsed.notes?.trim() ? parsed.notes : undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Fuel"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Trip"
            required
            error={errors.tripId?.message}
            options={(trips || []).map((t) => ({ value: t.id, label: `${t.tripNumber} - ${t.origin} → ${t.destination}` }))}
            {...register('tripId')}
          />

          <Select
            label="Vehicle"
            required
            error={errors.vehicleId?.message}
            options={(vehicles || []).map((v) => ({ value: v.id, label: `${v.registrationNumber} - ${v.make} ${v.model}` }))}
            {...register('vehicleId')}
            disabled={true}
          />

          <Input
            label="Fuel Date"
            required
            type="date"
            error={errors.fuelDate?.message}
            {...register('fuelDate')}
          />

          <Select
            label="Transaction Type"
            required
            error={errors.transactionType?.message}
            options={[
              { value: TransactionType.ISSUED, label: 'Issued' },
              { value: TransactionType.PURCHASED, label: 'Purchased' },
            ]}
            {...register('transactionType')}
          />

          <Input
            label="Quantity (Liters)"
            required
            type="number"
            step="0.01"
            error={errors.quantityLiters?.message}
            {...register('quantityLiters')}
          />

          <Input
            label="Cost Per Liter"
            required
            type="number"
            step="0.01"
            error={errors.costPerLiter?.message}
            {...register('costPerLiter')}
          />
        </div>

        <Input
          label="Notes"
          placeholder="e.g., Filled at Shell Station"
          error={errors.notes?.message}
          {...register('notes')}
        />

        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
};
